import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import SearchIcon from '@mui/icons-material/Search'
import {
  Box,
  Chip,
  InputAdornment,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { format, parseISO } from 'date-fns'

import { toast } from '../../components/LsToast'
import { ScreenWrapper } from '../../components/ScreenWrapper'
import { Path } from '../../router/BaseRouter/enums'
import { getPosClosings } from '../../service/pos-closings.service'
import { getReceipts } from '../../service/receipts.service'
import { ReceiptDto, ReceiptStatus, TimelineItem } from '../../service/types'
import { drawerMarginSx } from '../../utils/drawerMargin'
import { filterTimelineItems } from './filterReceipts'
import { DRAWER_WIDTH, ReceiptDetailDrawer } from './ReceiptDetailDrawer'

export const Receipts: FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { receiptId } = useParams<{ receiptId?: string }>()
  const [timelineItems, setTimelineItems] = useState<TimelineItem[]>([])
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [receiptsRes, closingsRes] = await Promise.all([getReceipts(), getPosClosings()])
      const receiptItems: TimelineItem[] = receiptsRes.data.map((r) => ({ type: 'receipt' as const, data: r }))
      const closingItems: TimelineItem[] = closingsRes.data.map((c) => ({ type: 'posClosing' as const, data: c }))
      const merged = [...receiptItems, ...closingItems].sort(
        (a, b) => new Date(b.data.createdAt).getTime() - new Date(a.data.createdAt).getTime(),
      )
      setTimelineItems(merged)
    } catch {
      toast.error(t('general.notification.unexpectedError'))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    void loadData()
  }, [loadData])

  const filteredItems = useMemo(() => filterTimelineItems(timelineItems, searchQuery), [timelineItems, searchQuery])

  const drawerReceipt = useMemo(() => {
    const item = timelineItems.find(
      (i): i is TimelineItem & { type: 'receipt' } => i.type === 'receipt' && i.data.id === receiptId,
    )
    return item?.data ?? null
  }, [timelineItems, receiptId])
  const drawerOpen = drawerReceipt !== null

  const handleRowClick = (receipt: ReceiptDto) => {
    if (receiptId === receipt.id) {
      navigate(Path.RECEIPTS)
    } else {
      navigate(`${Path.RECEIPTS}/${receipt.id}`)
    }
  }

  const formatDate = (dateStr: string) => {
    try {
      return format(parseISO(dateStr), 'dd.MM.yyyy HH:mm')
    } catch {
      return dateStr
    }
  }

  const getStatusColor = (status: ReceiptStatus): 'success' | 'error' => {
    return status === ReceiptStatus.Paid ? 'success' : 'error'
  }

  return (
    <>
      <ScreenWrapper
        title={t('receipts.title')}
        sx={drawerMarginSx(drawerOpen, DRAWER_WIDTH)}
        filter={
          <Box display="flex" alignItems="center" gap={2}>
            <TextField
              size="small"
              placeholder={t('receipts.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ backgroundColor: 'background.paper' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        }
      >
        {loading && <LinearProgress sx={{ mt: 2 }} />}
        <TableContainer component={Paper} sx={{ mt: loading ? 0 : 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('receipts.receiptNumber')}</TableCell>
                <TableCell>{t('receipts.customer')}</TableCell>
                <TableCell>{t('receipts.date')}</TableCell>
                <TableCell align="center">{t('receipts.bookings')}</TableCell>
                <TableCell align="right">{t('receipts.totalPrice')}</TableCell>
                <TableCell>{t('receipts.paymentType')}</TableCell>
                <TableCell>{t('receipts.status')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && filteredItems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary">
                      {t('receipts.noReceipts')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {filteredItems.map((item) =>
                item.type === 'receipt' ? (
                  <TableRow
                    key={item.data.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleRowClick(item.data)}
                  >
                    <TableCell>{item.data.receiptNumber}</TableCell>
                    <TableCell>{item.data.customer?.name ?? '\u2014'}</TableCell>
                    <TableCell>{formatDate(item.data.createdAt)}</TableCell>
                    <TableCell align="center">{item.data.bookingCount}</TableCell>
                    <TableCell align="right">{item.data.totalPrice.toFixed(2)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={t(`paymentType.${item.data.paymentType}`)} />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={t(`receiptStatus.${item.data.status}`)}
                        color={getStatusColor(item.data.status)}
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow
                    key={`closing-${item.data.id}`}
                    hover
                    sx={(theme) => ({
                      cursor: 'pointer',
                      backgroundColor: alpha(theme.palette.info.main, 0.08),
                    })}
                    onClick={() => navigate(Path.POS_CLOSINGS)}
                  >
                    <TableCell colSpan={2}>{t('receipts.posClosing')}</TableCell>
                    <TableCell>{formatDate(item.data.createdAt)}</TableCell>
                    <TableCell colSpan={4} />
                  </TableRow>
                ),
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </ScreenWrapper>

      <ReceiptDetailDrawer
        open={drawerOpen}
        onClose={() => navigate(Path.RECEIPTS)}
        receipt={drawerReceipt}
        onReceiptUpdated={loadData}
      />
    </>
  )
}
